import React, { useState, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './config';

const overlay = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.6)',
  zIndex: 10000,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const modal = {
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border-color)',
  borderRadius: '12px',
  padding: '28px',
  width: '760px',
  maxWidth: '96vw',
  maxHeight: '92vh',
  overflowY: 'auto',
  color: 'var(--text-primary)',
};

const btn = (bg, disabled = false) => ({
  padding: '8px 18px',
  background: disabled ? 'var(--bg-tertiary)' : bg,
  color: disabled ? 'var(--text-tertiary)' : 'var(--text-inverse)',
  border: 'none',
  borderRadius: '6px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: '13px',
  fontWeight: '500',
  marginRight: '8px',
});

const inputSt = {
  width: '100%',
  padding: '8px 12px',
  boxSizing: 'border-box',
  background: 'var(--bg-primary)',
  border: '1px solid var(--border-color)',
  borderRadius: '6px',
  color: 'var(--text-primary)',
  fontSize: '13px',
};

const STATUS_STYLE = {
  mapped: { bg: 'var(--success-color)22', fg: 'var(--success-color)', label: 'Mapped' },
  partial: { bg: 'var(--warning-color, #f59e0b)22', fg: 'var(--warning-color, #f59e0b)', label: 'Review' },
  unmapped: { bg: 'var(--error-color)22', fg: 'var(--error-color)', label: 'TODO' },
  schedule: { bg: 'var(--info-color)22', fg: 'var(--info-color)', label: 'Schedule' },
};

export default function ZapierImportModal({ onClose, onImported, workspaceId }) {
  const [source, setSource] = useState('zapier'); // zapier | make | n8n | pipedream
  const [rawJson, setRawJson] = useState('');
  const [name, setName] = useState('');
  const [report, setReport] = useState(null);     // coverage report from dry-run
  const [previewName, setPreviewName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const fileRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const parseInput = () => {
    let data;
    try {
      data = JSON.parse(rawJson);
    } catch {
      setError('Invalid JSON — paste the full Zapier/Make export, or drop the exported file.');
      return null;
    }
    setError('');
    return data;
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setRawJson(String(ev.target.result || ''));
    reader.readAsText(file);
  };

  const preview = async () => {
    const data = parseInput();
    if (data === null) return;
    setLoading(true);
    setReport(null);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/workflows/import/${source}`,
        { data, workspace_id: workspaceId || null, name: name.trim() || null, dry_run: true },
        { headers },
      );
      setReport(res.data.report);
      setPreviewName(res.data.name);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not parse this export. Check the format and try again.');
    } finally {
      setLoading(false);
    }
  };

  const create = async () => {
    const data = parseInput();
    if (data === null) return;
    setCreating(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/workflows/import/${source}`,
        { data, workspace_id: workspaceId || null, name: name.trim() || null, dry_run: false },
        { headers },
      );
      onImported?.(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Import failed. Check server logs for details.');
      setCreating(false);
    }
  };

  const reset = () => { setReport(null); setError(''); };

  const SourceTab = ({ id, label, hint }) => (
    <button
      onClick={() => { setSource(id); reset(); }}
      style={{
        flex: 1, padding: '10px', borderRadius: '8px', cursor: 'pointer',
        border: `1px solid ${source === id ? 'var(--accent-color)' : 'var(--border-color)'}`,
        background: source === id ? 'var(--accent-color)18' : 'var(--bg-primary)',
        color: 'var(--text-primary)', textAlign: 'left',
      }}
    >
      <div style={{ fontWeight: 600, fontSize: '14px' }}>{label}</div>
      <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px' }}>{hint}</div>
    </button>
  );

  return (
    <div style={overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modal}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px' }}>⚡ Import from Zapier / Make</h2>
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-tertiary)' }}>
              Convert an exported Zap or Make scenario into a xavvy draft workflow
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        {/* Source picker */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
          <SourceTab id="zapier" label="Zapier" hint="Paste a Zap export (.json)" />
          <SourceTab id="make" label="Make" hint="Paste a scenario blueprint (.json)" />
          <SourceTab id="n8n" label="n8n" hint="Paste a workflow export (.json)" />
          <SourceTab id="pipedream" label="Pipedream" hint="Paste a workflow export (.json)" />
        </div>

        {!report ? (
          <>
            <div style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '12px 14px', marginBottom: '14px', fontSize: '12.5px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              Steps are mapped to the closest xavvy node type and laid out on the canvas.
              Unmapped steps become <code>TODO:</code> placeholders. <strong>Credentials are never imported</strong> —
              each node will need its credential connected before the workflow can run.
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                Workflow Name <span style={{ color: 'var(--text-tertiary)' }}>(optional — taken from the export if blank)</span>
              </label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="My migrated workflow" style={inputSt} />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                  {{ zapier: 'Zapier', make: 'Make', n8n: 'n8n', pipedream: 'Pipedream' }[source]} Export JSON <span style={{ color: 'var(--error-color)' }}>*</span>
                </label>
                <button onClick={() => fileRef.current?.click()} style={{ ...btn('var(--bg-tertiary)'), marginRight: 0, padding: '4px 10px', fontSize: '11px', color: 'var(--text-primary)' }}>
                  📁 Choose file…
                </button>
                <input ref={fileRef} type="file" accept=".json,application/json" style={{ display: 'none' }} onChange={onFile} />
              </div>
              <textarea
                value={rawJson}
                onChange={(e) => setRawJson(e.target.value)}
                spellCheck={false}
                placeholder={{
                  zapier: '{\n  "title": "My Zap",\n  "nodes": [ { "selected_api": "GmailV2API", "type": "read", "action": "new_email" }, ... ]\n}',
                  make: '{\n  "name": "My Scenario",\n  "flow": [ { "id": 1, "module": "gmail:TriggerNewEmail" }, ... ]\n}',
                  n8n: '{\n  "name": "My Workflow",\n  "nodes": [ { "name": "Webhook", "type": "n8n-nodes-base.webhook" }, ... ],\n  "connections": { ... }\n}',
                  pipedream: '{\n  "name": "My Workflow",\n  "steps": [ { "name": "trigger", "namespace": "trigger", "key": "webhook" }, ... ]\n}',
                }[source]}
                style={{ ...inputSt, fontFamily: 'monospace', fontSize: '12px', height: '200px', resize: 'vertical' }}
              />
            </div>

            {error && (
              <div style={{ padding: '10px 14px', borderRadius: '6px', marginBottom: '14px', background: 'var(--error-color)22', color: 'var(--error-color)', border: '1px solid var(--error-color)', fontSize: '13px' }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={btn('var(--bg-tertiary)')}>Cancel</button>
              <button onClick={preview} disabled={loading || !rawJson.trim()} style={btn('var(--accent-color)', loading || !rawJson.trim())}>
                {loading ? '⏳ Analyzing…' : '🔍 Preview Mapping'}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Summary chips */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
              {[
                { label: 'Total Steps', value: report.total, color: 'var(--text-primary)' },
                { label: 'Mapped', value: report.mapped, color: 'var(--success-color)' },
                { label: 'Needs Review', value: report.partial, color: 'var(--warning-color, #f59e0b)' },
                { label: 'TODO', value: report.unmapped, color: 'var(--error-color)' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ flex: 1, minWidth: '110px', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '10px 14px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{label}</div>
                  <div style={{ fontSize: '22px', fontWeight: 700, color }}>{value}</div>
                </div>
              ))}
            </div>

            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '10px' }}>
              Will create draft workflow <strong style={{ color: 'var(--text-primary)' }}>{previewName}</strong>
              {report.cron_schedule ? <> · schedule <code>{report.cron_schedule}</code></> : null}
            </div>

            {/* Per-step table */}
            <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'hidden', marginBottom: '16px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-primary)', color: 'var(--text-tertiary)', textAlign: 'left' }}>
                    <th style={{ padding: '8px 10px', width: '34px' }}>#</th>
                    <th style={{ padding: '8px 10px' }}>Source</th>
                    <th style={{ padding: '8px 10px' }}>→ xavvy Node</th>
                    <th style={{ padding: '8px 10px', width: '78px' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {report.steps.map((s) => {
                    const st = STATUS_STYLE[s.status] || STATUS_STYLE.partial;
                    return (
                      <tr key={s.index} style={{ borderTop: '1px solid var(--border-color)' }} title={s.note}>
                        <td style={{ padding: '7px 10px', color: 'var(--text-tertiary)' }}>{s.index}</td>
                        <td style={{ padding: '7px 10px' }}>
                          <div style={{ color: 'var(--text-primary)' }}>{s.source_app || '—'}</div>
                          <div style={{ color: 'var(--text-tertiary)', fontSize: '11px' }}>{s.source_action || ''}</div>
                        </td>
                        <td style={{ padding: '7px 10px', fontFamily: 'monospace', color: 'var(--text-secondary)' }}>
                          {s.node_type || '—'}{s.action ? ` · ${s.action}` : ''}
                        </td>
                        <td style={{ padding: '7px 10px' }}>
                          <span style={{ padding: '2px 8px', borderRadius: '10px', background: st.bg, color: st.fg, fontSize: '11px', fontWeight: 600 }}>{st.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {error && (
              <div style={{ padding: '10px 14px', borderRadius: '6px', marginBottom: '14px', background: 'var(--error-color)22', color: 'var(--error-color)', border: '1px solid var(--error-color)', fontSize: '13px' }}>
                {error}
              </div>
            )}

            <div style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '12px 14px', marginBottom: '16px', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              The workflow will be created <strong>paused</strong> as a <strong>draft</strong>. Connect credentials,
              confirm the steps marked <em>Review</em>, replace any <em>TODO</em> placeholders, then publish and unpause.
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button onClick={reset} style={btn('var(--bg-tertiary)')}>← Back</button>
              <button onClick={create} disabled={creating} style={btn('var(--accent-color)', creating)}>
                {creating ? '⏳ Creating…' : '✅ Create Draft Workflow'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
